import { getSessionId } from '@aspendental/shared-utils/utils';
import { useMarketingSource } from '@aspendental/web-shared-utils';
import * as Sentry from '@sentry/nextjs';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import { useRouter } from 'next/router';
import { appWithTranslation } from 'next-i18next';
import React, { useEffect } from 'react';
import { ContextProviders } from '@src/components/shared/providers/ContextProviders';
import { Layout } from '@src/components/templates/layout';
import { getLogger } from '@src/lib/logging/logger';
import { Session, useInitialReferrer, useSalesforceMCP } from '@src/lib/shared-components-web';
import '@src/lib/shared-components-web/assets/css/dynamic-form.css';
import '@src/lib/shared-components-web/fonts.css';
import '@src/lib/shared-components-web/global.css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/pagination';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/navigation';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
import { toggleLogRocket, isLogRocketEnabled } from '@src/services/logRocket';
import { toggleSentry, isSentryEnabled } from '@src/services/sentry';
import { initializeLogRocket } from '@utils/pages/logRocketInit';
import { initializeSentryClient } from '@utils/pages/pageSentryClientInit';
export function reportWebVitals({
  id,
  name,
  label,
  value
}: NextWebVitalsMetric) {
  const logger = getLogger('client');
  logger.info({
    name,
    category: label === 'web-vital' ? 'Web Vitals' : 'Next.js custom metric',
    value: Math.round(name === 'CLS' ? value * 1000 : value),
    // values must be integers
    id // id unique to current page load
  });
}
type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};
const App = ({
  Component,
  pageProps
}: AppPropsWithLayout) => {
  const router = useRouter();
  const config = pageProps?.config as BrandThemeConfig;
  const {
    features,
    featureFlags
  } = config ?? {};
  const brandName = config?.name || 'Lovet';
  const brazeEnabled = features?.analytics?.braze?.enabled;
  const brazeSdkKey = features?.analytics?.braze?.sdkKey;
  const brazeBaseUrl = features?.analytics?.braze?.baseUrl;
  const environment = pageProps?.env?.ENVIRONMENT_NAME || 'prod';
  toggleSentry(!!pageProps?.config?.features?.sentry?.enabled);
  toggleLogRocket(!!features?.logRocket?.enabled);

  // regular page and blog page
  const disableStickyFooterOnMobile = pageProps?.page?.disableStickyFooterOnMobile || pageProps?.pageData?.disableStickyFooterOnMobile;
  // regular page and blog page
  const disableStickyFooterOnDesktop = pageProps?.page?.disableStickyFooterOnDesktop || pageProps?.pageData?.disableStickyFooterOnDesktop;
  const disableHeaderTopAlertBanner = pageProps?.page?.disableHeaderTopAlertBanner || pageProps?.pageData?.disableHeaderTopAlertBanner;
  const disableHeaderBottomAlertBanner = pageProps?.page?.disableHeaderBottomAlertBanner || pageProps?.pageData?.disableHeaderBottomAlertBanner;
  const logoHref = '/';
  useEffect(() => {
    console.info('Lovet Combined OD & DM');
  }, []);
  useEffect(() => {
    if (brazeEnabled) {
      import('@utils/pages/braze-exports').then(({
        initialize,
        openSession,
        automaticallyShowInAppMessages
      }) => {
        initialize(brazeSdkKey, {
          baseUrl: brazeBaseUrl,
          enableLogging: false,
          // Only enable logging in development environment
          allowUserSuppliedJavascript: true
        });
        automaticallyShowInAppMessages();
        openSession();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brazeEnabled]);

  // get  set referrer for marketing payload.
  useInitialReferrer();

  // Capture marketing_source cookie from landing URL query params.
  useMarketingSource({
    enableCcTransformation: featureFlags?.enableCcMarketingSourceTransformation ?? false
  });
  const salesforceMCPConfig = pageProps?.appData?.config?.salesforceMCP;
  useSalesforceMCP(salesforceMCPConfig?.enabled ? {
    account: salesforceMCPConfig.account,
    dataset: salesforceMCPConfig.dataset,
    cookieDomain: salesforceMCPConfig.cookieDomain
  } : null);
  useEffect(() => {
    if (isSentryEnabled()) {
      initializeSentryClient(environment);
      Sentry.setTag('brand', brandName);
      Sentry.setContext('user', {
        sessionId: getSessionId(),
        brand: brandName
      });
    }
  }, [brandName, environment]);
  useEffect(() => {
    if (isLogRocketEnabled() && features?.logRocket?.projectId) {
      initializeLogRocket(environment, features.logRocket.projectId);
    }
  }, [brandName, environment, features?.logRocket?.projectId]);

  // Dev-only: warn if a page mounts the default Layout but its
  // getServerSideProps did not populate `serverFeatureFlags`. Without this
  // wiring, layout-level VWO flags (e.g. showNewHeader) silently default to
  // off on that route regardless of the experiment.
  useEffect(() => {
    const envNotProd = process.env.NODE_ENV !== 'production';
    if (envNotProd && !Component.getLayout && pageProps?.serverFeatureFlags === undefined) {
      console.debug('[VWO FME] pageProps.serverFeatureFlags is undefined on a page that mounts the default Layout. ' + "Add `resolveFeatureFlags(LAYOUT_FEATURE_FLAGS, ...)` to this page's getServerSideProps; " + 'layout-level flags will fall back to disabled.');
    }
  }, [Component, pageProps?.serverFeatureFlags]);
  useEffect(() => {
    // when component is mounting we remove server side rendered css:
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles?.parentNode) {
      jssStyles.remove();
    }
    router.events.on('routeChangeComplete', () => null);
    return () => {
      router.events.off('routeChangeComplete', () => null);
    };
  }, [router.events]);
  const getLayout = Component.getLayout ?? (page => {
    return <Layout appData={pageProps.appData} isFooterLite={features.footer.lite} isFooterAlt={features.footer.alt} withSimpleLayout={features.footer.withSimpleLayout} footerData={pageProps.appData?.footer} showHeaderNavMenu={featureFlags?.showHeaderNavigationMenu || false} breadcrumbsData={pageProps.breadcrumbsData} withFixedHeader={!features.header.hideHeaderOnScroll} disableStickyFooterOnMobile={disableStickyFooterOnMobile} disableStickyFooterOnDesktop={disableStickyFooterOnDesktop} hideLocation={featureFlags?.hideHeaderFacilityLocator ?? false} hidePrimaryActionButton={featureFlags?.hideHeaderSchedulingButton ?? pageProps.page?.hideScheduleButtonInHeader ?? false} disableHeaderTopAlertBanner={disableHeaderTopAlertBanner} disableHeaderBottomAlertBanner={disableHeaderBottomAlertBanner} logoHref={logoHref}>
					{page}
				</Layout>;
  });

  // check to see if there is a config if there is no config the app cannot load.
  // if there is no config try to do a reload to get the config from the server
  // limit reloads to 1 time
  if (!config) {
    const hasPageReloaded = globalThis?.sessionStorage?.getItem('hasPageReloaded');
    if (!hasPageReloaded) {
      globalThis?.sessionStorage?.setItem('hasPageReloaded', '1');
      globalThis.location.reload();
    }
    return <div>
				{' '}
				This page could not be loaded <a href="/">Return to Home</a>
			</div>;
  }
  globalThis?.sessionStorage?.removeItem('hasPageReloaded');
  return <Session data-sentry-element="Session" data-sentry-component="App" data-sentry-source-file="_app.page.tsx">
			<ContextProviders config={pageProps.config} preview={pageProps.preview} environment={environment} user={pageProps.user} pageTheme={pageProps?.page?.pageTheme} defaultToCdn={pageProps?.config?.featureFlags?.defaultToCdn} deviceType={pageProps.deviceType} hideHeaderFacilityLocator={featureFlags?.hideHeaderFacilityLocator} stateCodeFromHeaders={pageProps.stateCodeFromHeaders} facilityCode={pageProps?.facilityCode} facilityContext={pageProps?.facilityContext} data-sentry-element="ContextProviders" data-sentry-source-file="_app.page.tsx">
				{getLayout(<Component {...pageProps} />)}
			</ContextProviders>
		</Session>;
};
export default appWithTranslation(App);